import React, { useEffect, useState } from 'react';
import { machineryService } from '../../services/machineryService';
import { formatDateLocaleLima } from '../../utils/dateUtils';

const DOC_LABELS = {
  soat: 'SOAT',
  revision_tecnica: 'Revisión técnica',
};

const emptyRenewal = () => ({ expires_at: '', issued_at: '', notes: '' });

const VehicleCompliancePanel = ({ vehicleId, currentSoat, currentRevision, onUpdated }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState(null);
  const [error, setError] = useState('');
  const [renewals, setRenewals] = useState({
    soat: emptyRenewal(),
    revision_tecnica: emptyRenewal(),
  });

  const loadCompliance = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await machineryService.getVehicleCompliance(vehicleId);
      if (res.success && res.data) {
        setHistory(Array.isArray(res.data.history) ? res.data.history : []);
      } else {
        setError(res.message || 'No se pudo cargar el historial');
      }
    } catch {
      setError('Error al cargar documentación del vehículo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicleId) loadCompliance();
  }, [vehicleId]);

  const handleRenewalChange = (docType, field, value) => {
    setRenewals((prev) => ({
      ...prev,
      [docType]: { ...prev[docType], [field]: value },
    }));
  };

  const handleRenew = async (docType) => {
    const data = renewals[docType];
    if (!data.expires_at) {
      setError(`Indique la nueva fecha de vencimiento de ${DOC_LABELS[docType]}`);
      return;
    }
    setSavingType(docType);
    setError('');
    try {
      const res = await machineryService.createVehicleComplianceRecord(vehicleId, {
        document_type: docType,
        expires_at: data.expires_at,
        issued_at: data.issued_at || null,
        notes: data.notes || null,
      });
      if (res.success) {
        setRenewals((prev) => ({ ...prev, [docType]: emptyRenewal() }));
        await loadCompliance();
        if (onUpdated) onUpdated();
      } else {
        setError(res.message || 'No se pudo registrar la renovación');
      }
    } catch {
      setError('Error al registrar la renovación');
    } finally {
      setSavingType(null);
    }
  };

  const renderDocBlock = (docType, currentDate) => {
    const renewal = renewals[docType];
    const docHistory = history.filter((row) => row.document_type === docType);
    const isExpired = currentDate && new Date(currentDate) < new Date(new Date().toDateString());

    return (
      <div key={docType} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h4 className="text-sm font-semibold text-gray-800">{DOC_LABELS[docType]}</h4>
          {currentDate ? (
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${isExpired ? 'bg-red-100 text-red-800' : 'bg-teal-100 text-teal-800'}`}>
              Vence: {formatDateLocaleLima(currentDate)}{isExpired ? ' (vencido)' : ''}
            </span>
          ) : (
            <span className="text-xs text-gray-500">Sin fecha registrada</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Nueva fecha de vencimiento *</label>
            <input
              type="date"
              value={renewal.expires_at}
              onChange={(e) => handleRenewalChange(docType, 'expires_at', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de emisión (opcional)</label>
            <input
              type="date"
              value={renewal.issued_at}
              onChange={(e) => handleRenewalChange(docType, 'issued_at', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notas (opcional)</label>
            <input
              type="text"
              value={renewal.notes}
              onChange={(e) => handleRenewalChange(docType, 'notes', e.target.value)}
              placeholder="Ej. Aseguradora, centro ITV..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleRenew(docType)}
          disabled={savingType === docType}
          className="px-4 py-2 text-sm bg-teal-600 hover:bg-teal-700 disabled:bg-gray-400 text-white rounded-lg font-medium"
        >
          {savingType === docType ? 'Guardando...' : `Registrar renovación de ${DOC_LABELS[docType]}`}
        </button>

        {docHistory.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-gray-600 mb-2">Historial</p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden bg-white">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Vencimiento</th>
                    <th className="px-3 py-2 text-left">Emisión</th>
                    <th className="px-3 py-2 text-left">Notas</th>
                    <th className="px-3 py-2 text-left">Registrado</th>
                  </tr>
                </thead>
                <tbody>
                  {docHistory.map((row) => (
                    <tr key={row.id} className="border-t border-gray-100">
                      <td className="px-3 py-2">{row.expires_at ? formatDateLocaleLima(row.expires_at) : '—'}</td>
                      <td className="px-3 py-2">{row.issued_at ? formatDateLocaleLima(row.issued_at) : '—'}</td>
                      <td className="px-3 py-2">{row.notes || '—'}</td>
                      <td className="px-3 py-2">{row.created_at ? formatDateLocaleLima(row.created_at.split('T')[0]) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="md:col-span-2 border-t border-gray-200 pt-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">SOAT y revisión técnica</label>
      <p className="text-sm text-gray-500 mb-4">
        El sistema alertará en el dashboard un mes antes del vencimiento. Si ya venció, registre una nueva fecha de renovación.
      </p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading ? (
        <p className="text-sm text-gray-500">Cargando historial...</p>
      ) : (
        <div className="space-y-4">
          {renderDocBlock('soat', currentSoat)}
          {renderDocBlock('revision_tecnica', currentRevision)}
        </div>
      )}
    </div>
  );
};

export default VehicleCompliancePanel;
